import { PhoneForwardedIcon, XCircleIcon } from 'lucide-react';

import { EscalationCallout } from '@/app/(app)/voice-agent/tab-routing';
import { ToggleButton } from '@/components/atoms/toggle-button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useVoiceSettingsCtx } from './provider';

export function ExistingCustomerSettings() {
  const {
    settings: { customer_identification },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<typeof customer_identification>) => {
    onSettingsChange({ customer_identification: { ...customer_identification, ...v } });
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <Card>
        <CardHeader>
          <CardTitle>Initial Customer Query</CardTitle>
          <CardDescription>Determine if the caller is new or existing at the start of the call. </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex w-full items-center gap-3 rounded-lg border p-4'>
            <div className='space-y-1'>
              <Label>Ask if first time caller</Label>
              <div className='text-muted-foreground text-sm'>
                If enabled, the agent will ask the caller if this is their first time calling.
              </div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.ask_if_new_customer}
              onCheckedChange={(checked) => onChange({ ask_if_new_customer: checked })}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Customer Identification Methods</CardTitle>
          <CardDescription>Define how existing customers are identified when they call.</CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Caller&apos;s phone number</Label>
              <div className='text-muted-foreground text-sm'>
                Identify customer by their incoming phone number (Caller ID).
              </div>
            </div>
            <Switch
              className='ml-auto'
              disabled={true}
              checked={customer_identification.use_phone_number}
              onCheckedChange={(checked) => onChange({ use_phone_number: checked })}
            />
          </div>
          <Separator />
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Address</Label>
              <div className='text-muted-foreground text-sm'>
                Ask the customer for their street address and ZIP code.
              </div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.use_address}
              onCheckedChange={(checked) => onChange({ use_address: checked })}
            />
          </div>
          <Separator />
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Secondary phone number</Label>
              <div className='text-muted-foreground text-sm'>
                Prompt the customer to provide a phone number associated with their account.
              </div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.use_other_phone_number}
              onCheckedChange={(checked) => onChange({ use_other_phone_number: checked })}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Existing Customer Verification</CardTitle>
          <CardDescription>
            Configure what information Voice Agent should verify with identified customers.{' '}
          </CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
        <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Verify Customer Name</Label>
              <div className='text-muted-foreground text-sm'>Confirm the customer&apos;s name for verification purposes.</div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.confirm_name}
              onCheckedChange={(checked) => onChange({ confirm_name: checked })}
            />
          </div>
          <Separator />
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Customer&apos;s Service Address</Label>
              <div className='text-muted-foreground text-sm'>Important for multi-property customers.</div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.confirm_address}
              onCheckedChange={(checked) => onChange({ confirm_address: checked })}
            />
          </div>
          <Separator />
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Verify Contact Number</Label>
              <div className='text-muted-foreground text-sm'>Update if the number has changed. </div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.confirm_contact_number}
              onCheckedChange={(checked) => onChange({ confirm_contact_number: checked })}
            />
          </div>
          <Separator />
          <div className='flex w-full items-center'>
            <div className='space-y-1'>
              <Label>Verify Email Address</Label>
              <div className='text-muted-foreground text-sm'>Confirm or update the customer&apos;s email address.</div>
            </div>
            <Switch
              className='ml-auto'
              checked={customer_identification.confirm_email}
              onCheckedChange={(checked) => onChange({ confirm_email: checked })}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Account Not Found</CardTitle>
          <CardDescription>Define what happens if an existing customer&apos;s account cannot be found.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col gap-3'>
            <Label className='px-1'>How should this action be handled?</Label>
            <div className='grid grid-cols-2 gap-2'>
              <ToggleButton
                selected={customer_identification.not_found_action === 'NOTIFY_REPRESENTATIVE'}
                toggle={() => onChange({ not_found_action: 'NOTIFY_REPRESENTATIVE' })}
                layout='vertical'
                className='py-4'
              >
                <PhoneForwardedIcon className='size-4' />
                Notify Representative
              </ToggleButton>
              <ToggleButton
                selected={customer_identification.not_found_action === 'DO_NOTHING'}
                toggle={() => onChange({ not_found_action: 'DO_NOTHING' })}
                layout='vertical'
                className='py-4'
              >
                <XCircleIcon className='size-4' />
                Do Nothing
              </ToggleButton>
            </div>
            {customer_identification.not_found_action === 'NOTIFY_REPRESENTATIVE' && (
              <div className='mt-3'>
                <EscalationCallout />
                <div className='mt-4 space-y-2'>
                  <Label className='px-1'>Script</Label>
                  <Textarea
                    placeholder='Enter custom notification script...'
                    value={customer_identification.not_found_script ?? ''}
                    onChange={(e) => onChange({ not_found_script: e.target.value })}
                    className='min-h-[100px]'
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
